import React from 'react';
import { getProcess, resolveLatestProcessName } from '../../transactions/transaction';
import { FormattedMessage, useIntl } from 'react-intl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { LINE_ITEM_CUSTOMER_COMMISSION, LINE_ITEM_PROVIDER_COMMISSION } from '../../util/types';
import { useConfiguration } from '../../context/configurationContext';
import { estimatedTotalPrice } from '../../util/booking';

import css from './OrderBreakdown.module.css';
import classNames from 'classnames';

const { Money } = sdkTypes;

/**
 * Checks if line item represents commission
 */
const isCommission = lineItem => {
  return (
    lineItem.code === LINE_ITEM_PROVIDER_COMMISSION ||
    lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION
  );
};

/**
 * Displays the provider's total earnings in the order breakdown.
 *
 * The component calculates the provider's final earnings by combining
 * provider-related transaction line items with the provider commission.
 * It also adapts the displayed label based on the transaction state
 * (default, completed, or refunded).
 *
 * If the transaction is refunded, the displayed total becomes 0.
 * The component renders only for providers.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.transaction - Transaction entity containing process and transition information.
 * @param {Array<Object>} props.txLineItems - Transaction line items associated with the booking.
 * @param {Array<Object>} props.depositLineItems - Deposit-related line items that may include commissions.
 * @param {boolean} props.refunded - Indicates whether the transaction was refunded.
 * @param {string} props.currency - Currency code used for formatting the total price.
 * @param {boolean} props.isProvider - Determines whether the current user is the provider.
 *
 * @returns {JSX.Element|null} Provider earnings line item or null if not visible.
 */
const LineItemArtistEarningMaybe = props => {
  const { transaction, txLineItems, depositLineItems, refunded, currency, isProvider } = props;
  const intl = useIntl();
  const config = useConfiguration();

  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName || !txLineItems?.length) {
    return null;
  }
  if (!isProvider) return null;

  const process = getProcess(processName);
  const isCompleted = process.isCompleted(transaction?.attributes?.lastTransition);
  const isRefunded = process.isRefunded(transaction?.attributes?.lastTransition);

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }
  const providerCommision = depositLineItems?.find(isCommission);

  const providerLineItems = txLineItems.filter(item => item.includeFor.includes('provider'));
  const lineItemsFinal = [...providerLineItems, providerCommision];
  const totalPrice = refunded
    ? new Money(0, config.currency)
    : estimatedTotalPrice(lineItemsFinal, currency);

  return (
    <div className={css.lineItemTotal}>
      <span className={classNames(css.totalLabel, css.bold)}>
        {providerTotalMessageId && <FormattedMessage id={providerTotalMessageId} />}
      </span>
      <span className={css.totalPrice}>{formatMoney(intl, totalPrice)}</span>
    </div>
  );
};

export default LineItemArtistEarningMaybe;
